import { useQuery } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import { Button, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  BellOutlined,
  CalendarOutlined,
  FieldTimeOutlined,
  PlayCircleOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import { CalendarApi } from "../../services";
import { toPersianNumber } from "../../utils/helpers";
import AttendancePunch from "../AttendancePunch";
import CountUpTimer from "../CountUpTimer";
import styles from "./styles.module.css";

/** Mirrors CalendarEvent::TYPES on the API. */
const EVENT_MEETING = 1;
const EVENT_REMINDER = 2;

const todayIso = () => {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const clockLabel = (iso) => {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return toPersianNumber(
    `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`,
  );
};

const hoursLabel = (seconds) => {
  const total = Math.max(0, Math.round((seconds ?? 0) / 60));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;

  if (hours === 0) return `${toPersianNumber(minutes)} دقیقه`;
  if (minutes === 0) return `${toPersianNumber(hours)} ساعت`;
  return `${toPersianNumber(hours)} ساعت و ${toPersianNumber(minutes)} دقیقه`;
};

/**
 * "What does my day look like." Everything above this on the user dashboard is a
 * count of past work; this is the only band that answers what is happening right
 * now — the running timer, the time already logged today, and the meetings and
 * reminders still ahead.
 *
 * The calendar endpoint scopes itself to the signed-in user unless they can
 * manage projects, so no extra filtering is needed here.
 */
const TodayStrip = ({
  tracking,
  trackedSecondsToday,
  timeTrackingEnabled = true,
  attendanceEnabled = false,
  dataTour,
}) => {
  const navigate = useNavigate();
  const today = todayIso();

  const { data: events } = useQuery({
    queryKey: ["[Calendar] Today", today],
    queryFn: () =>
      CalendarApi.getCalendarEvents({
        params: { per_page: 100, start_at: today, end_at: today },
      }).then((result) => result.data.calendar_events),
    initialData: [],
  });

  const agenda = (events ?? [])
    .filter((event) => [EVENT_MEETING, EVENT_REMINDER].includes(event.type))
    .sort((a, b) => String(a.start_at).localeCompare(String(b.start_at)))
    .slice(0, 3);

  const taskHref = tracking?.project?.uuid
    ? `/projects/edit/${tracking.project.uuid}?tab=Kanban&task=${tracking.task?.uuid}`
    : null;

  /* Cells are collected rather than hard-placed: whichever modules are off,
     the grid below fits itself to however many are left instead of relying on
     a per-count override class. */
  return (
    <div className={`${styles.strip} ds-section`} data-tour={dataTour}>
      {timeTrackingEnabled && (
        <div className={styles.cell}>
          <span className={`${styles.icon} kpi-icon-green`}>
            <PlayCircleOutlined />
          </span>
          <div className={styles.body}>
            <span className={styles.label}>تایمر فعال</span>
            {tracking ? (
              <>
                <Tooltip title={tracking.task?.title}>
                  <span
                    className={`${styles.value} ${taskHref ? styles.valueLink : ""}`}
                    role={taskHref ? "button" : undefined}
                    tabIndex={taskHref ? 0 : undefined}
                    onClick={taskHref ? () => navigate(taskHref) : undefined}
                    onKeyDown={
                      taskHref
                        ? (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              navigate(taskHref);
                            }
                          }
                        : undefined
                    }
                  >
                    {tracking.task?.title}
                  </span>
                </Tooltip>
                <span className={styles.timer}>
                  <CountUpTimer
                    startDate={tracking.started_at}
                    fontSize={13}
                    color="var(--kpi-green-fg)"
                  />
                </span>
              </>
            ) : (
              <span className={styles.muted}>تایمری فعال نیست</span>
            )}
          </div>
        </div>
      )}

      {timeTrackingEnabled && (
        <div className={styles.cell}>
          <span className={`${styles.icon} kpi-icon-purple`}>
            <FieldTimeOutlined />
          </span>
          <div className={styles.body}>
            <span className={styles.label}>زمان ثبت‌شده‌ی امروز</span>
            <span className={styles.value}>{hoursLabel(trackedSecondsToday)}</span>
            <Link className={styles.link} to="/time-tracking">
              <ArrowLeftOutlined /> جزئیات
            </Link>
          </div>
        </div>
      )}

      <div className={styles.cell}>
        <span className={`${styles.icon} kpi-icon-blue`}>
          <CalendarOutlined />
        </span>
        <div className={styles.body}>
          <span className={styles.label}>برنامه‌ی امروز</span>
          {agenda.length === 0 ? (
            <span className={styles.muted}>جلسه یا یادآوری‌ای برای امروز نداری</span>
          ) : (
            <div className={styles.agenda}>
              {agenda.map((event) => (
                <span key={event.id} className={styles.agendaRow}>
                  {event.type === EVENT_MEETING ? (
                    <TeamOutlined className={styles.agendaIcon} />
                  ) : (
                    <BellOutlined className={styles.agendaIcon} />
                  )}
                  <span className={styles.agendaTitle}>{event.title}</span>
                  {clockLabel(event.start_at) && (
                    <span className={styles.agendaTime}>
                      {clockLabel(event.start_at)}
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}
          <Button
            type="link"
            size="small"
            className={styles.link}
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/calendar")}
          >
            تقویم
          </Button>
        </div>
      </div>

      {attendanceEnabled && <AttendancePunch variant="cell" />}
    </div>
  );
};

export default TodayStrip;
